import { defineStore } from "pinia"
import { ENTITY_PAGES } from "src/util/constants/configurations"
import { DarkModeOption, WorkActionOption } from "src/util/enums/configuration"
import {
  ConfigSortOrder,
  IssueClickOptions,
  IssueSortBy,
  NotPerformedEntityClickOptions,
  PerformedEntityClickOptions,
  PerformedWorkSortBy,
  WorkOrderClickOptions,
  WorkOrderIssueSortBy,
  WorkOrderItemSortBy,
  WorkOrderSortBy,
} from "src/util/enums/entityOverviewConfig"
import { TableBookmarks } from "src/util/types/components/sfxTable"
import { EntityOverviewConfig } from "src/util/types/dataModels/entityDefinition"

export interface UserSettings {
  // Present on user configuration page
  workOrderDefaultAction: WorkActionOption
  issueDefaultAction: WorkActionOption
  autoCompleteWork: boolean
  skipListWithSingleItem: boolean
  locationSelectItem: boolean
  internetConnectivity: boolean
  prioritizeData: boolean
  animateNotification: boolean
  darkMode: DarkModeOption
  tutorial: boolean
  language: string
  easterEggs: boolean
  displayWorkspaceCards: boolean
  displayLayoutCards: boolean
  tableBookmarks: TableBookmarks
  showLocationSection: Record<string, boolean>
  hideUnsubmittedWorkListDialog: Record<string, boolean>
  shownStagnantWorkDialogDate?: number
  downloadDataOnStartUp: boolean
}

export interface ConfigurationStoreType extends UserSettings {
  tutorialStatus: TutorialStatusType | Record<string, never>
  easterEggStatus: EasterEggStatusType | Record<string, never>
  entityOverviewConfig: EntityOverviewConfig
}

// Added junk typing so that interface is not empty
interface TutorialStatusType {
  loginShown: boolean
}

interface EasterEggStatusType {
  sheepBurning: boolean
  konamiCodeActive: boolean
  snakeGameScores: Array<snakeHighScoreEntry>
}

type snakeHighScoreEntry = { player: string; score: number }

export const useConfigurationStore = defineStore("pinia-configurationStore", {
  persist: true,

  state: (): ConfigurationStoreType => ({
    workOrderDefaultAction: WorkActionOption.CHOOSE,
    issueDefaultAction: WorkActionOption.CHOOSE,
    autoCompleteWork: false,
    skipListWithSingleItem: true,
    locationSelectItem: true,
    internetConnectivity: true,
    prioritizeData: true,
    animateNotification: false,
    darkMode: DarkModeOption.OFF,
    tutorial: true,
    language: "English",
    easterEggs: false,
    displayWorkspaceCards: true,
    displayLayoutCards: true,
    tableBookmarks: initBookmarks(),
    showLocationSection: {},
    hideUnsubmittedWorkListDialog: {},
    shownStagnantWorkDialogDate: undefined,
    downloadDataOnStartUp: true,

    tutorialStatus: {},
    easterEggStatus: {
      sheepBurning: false,
      konamiCodeActive: false,
      snakeGameScores: [],
    },
    entityOverviewConfig: {
      item: {
        issueSortBy: IssueSortBy.PRIORITY,
        issueSortOrder: ConfigSortOrder.DESCENDING,
        issueWhenClicked: IssueClickOptions.OPEN_OVERVIEW,
        workOrderSortBy: WorkOrderSortBy.DEADLINE,
        workOrderSortOrder: ConfigSortOrder.DESCENDING,
        workOrderWhenClicked: WorkOrderClickOptions.OPEN_OVERVIEW,
      },
      correctiveWork: {
        eventSortOrder: ConfigSortOrder.DESCENDING,
        commentSortOrder: ConfigSortOrder.DESCENDING,
      },
      regularWork: {
        issueSortBy: IssueSortBy.PRIORITY,
        issueSortOrder: ConfigSortOrder.DESCENDING,
        issueWhenClicked: IssueClickOptions.OPEN_OVERVIEW,
        eventSortOrder: ConfigSortOrder.DESCENDING,
        commentSortOrder: ConfigSortOrder.DESCENDING,
      },
      workOrderCorrective: {
        issueSortBy: WorkOrderIssueSortBy.DESIGNATION,
        issueSortOrder: ConfigSortOrder.DESCENDING,
        issueNotPerformedClicked: NotPerformedEntityClickOptions.OPEN_ENTITY_OVERVIEW,
        issuePerformedClicked: PerformedEntityClickOptions.OPEN_PERFORMED_WORK,
        issueHidePerformed: false,
        performedWorkSortBy: PerformedWorkSortBy.PERFORMED_DATE_TIME,
        performedWorkSortOrder: ConfigSortOrder.DESCENDING,
        commentSortOrder: ConfigSortOrder.DESCENDING,
      },
      workOrderRegular: {
        itemSortBy: WorkOrderItemSortBy.DESIGNATION,
        itemSortOrder: ConfigSortOrder.DESCENDING,
        itemNotPerformedClicked: NotPerformedEntityClickOptions.OPEN_ENTITY_OVERVIEW,
        itemPerformedClicked: PerformedEntityClickOptions.OPEN_PERFORMED_WORK,
        itemHidePerformed: false,
        performedWorkSortBy: PerformedWorkSortBy.PERFORMED_DATE_TIME,
        performedWorkSortOrder: ConfigSortOrder.DESCENDING,
        commentSortOrder: ConfigSortOrder.DESCENDING,
      },
    },
  }),

  getters: {
    // returns data applicable only to the user configuration page
    getCurrentConfiguration(): UserSettings {
      return {
        workOrderDefaultAction: this.workOrderDefaultAction,
        issueDefaultAction: this.issueDefaultAction,
        autoCompleteWork: this.autoCompleteWork,
        skipListWithSingleItem: this.skipListWithSingleItem,
        locationSelectItem: this.locationSelectItem,
        internetConnectivity: this.internetConnectivity,
        prioritizeData: this.prioritizeData,
        animateNotification: this.animateNotification,
        darkMode: this.darkMode,
        tutorial: this.tutorial,
        language: this.language,
        easterEggs: this.easterEggs,
        displayWorkspaceCards: this.displayWorkspaceCards,
        displayLayoutCards: this.displayLayoutCards,
        tableBookmarks: this.tableBookmarks,
        showLocationSection: this.showLocationSection,
        hideUnsubmittedWorkListDialog: this.hideUnsubmittedWorkListDialog,
        shownStagnantWorkDialogDate: this.shownStagnantWorkDialogDate,
        downloadDataOnStartUp: this.downloadDataOnStartUp,
      }
    },
  },

  actions: {
    updateConfigurations(configurations: UserSettings) {
      for (const [key, value] of Object.entries(configurations)) {
        ;(this[key as keyof ConfigurationStoreType] as keyof ConfigurationStoreType) = value
      }
    },
  },
})

function initBookmarks(): TableBookmarks {
  return Object.assign({}, ...ENTITY_PAGES.map((entity) => ({ [entity]: [] })))
}
